import { useCallback, useEffect, useSyncExternalStore } from 'react'
import {
    getSidebarTheme,
    updateSidebarTheme,
    type SidebarTheme,
} from '@/lib/api/utils/sidebar-theme-api'

/**
 * Tema do menu lateral (escuro com gradiente da marca ou claro), escolhido por
 * pessoa e salvo no backend (`Setting` com role USER, nome `sidebar_theme`).
 *
 * O valor fica em cache no `localStorage` junto com o id do usuário: recarregar
 * a página não faz requisição. A busca no backend só acontece quando o cache não
 * é dessa pessoa, ou seja, depois do login (o `logout` apaga a chave) ou quando
 * outra pessoa entrou no mesmo navegador.
 *
 * A rail do menu e o trilho do dock de atendimentos leem o mesmo estado, por isso
 * o store é de módulo e não um `useState` em cada componente.
 */
export const SIDEBAR_THEME_STORAGE_KEY = 'sidebar_theme'

interface CachedSidebarTheme {
    userId: number
    theme: SidebarTheme
}

const listeners = new Set<() => void>()
let current: CachedSidebarTheme | null = readCache()
let fetchingFor: number | null = null

function readCache(): CachedSidebarTheme | null {
    try {
        const raw = localStorage.getItem(SIDEBAR_THEME_STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (
            typeof parsed?.userId === 'number' &&
            (parsed.theme === 'dark' || parsed.theme === 'light')
        ) {
            return parsed
        }
    } catch {
        // cache corrompido ou storage bloqueado: segue sem cache
    }
    return null
}

function writeCache(value: CachedSidebarTheme) {
    current = value
    try {
        localStorage.setItem(SIDEBAR_THEME_STORAGE_KEY, JSON.stringify(value))
    } catch {
        // storage bloqueado: o tema vale só para esta aba
    }
    listeners.forEach(listener => listener())
}

function subscribe(listener: () => void) {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export function useSidebarTheme(userId: number | undefined) {
    const cached = useSyncExternalStore(subscribe, () => current)
    const isCachedForUser = !!userId && cached?.userId === userId
    const theme: SidebarTheme = isCachedForUser ? cached!.theme : 'dark'

    useEffect(() => {
        if (!userId || isCachedForUser || fetchingFor === userId) return
        fetchingFor = userId
        getSidebarTheme()
            .then(res => {
                if (res.success && res.result?.theme) {
                    writeCache({ userId, theme: res.result.theme })
                }
            })
            .catch(() => {})
            .finally(() => {
                if (fetchingFor === userId) fetchingFor = null
            })
    }, [userId, isCachedForUser])

    const setTheme = useCallback(
        (next: SidebarTheme) => {
            if (!userId) return
            const previous: CachedSidebarTheme =
                current?.userId === userId ? current : { userId, theme: 'dark' }
            writeCache({ userId, theme: next })
            // O `apiRequest` devolve `success: false` em vez de lançar.
            updateSidebarTheme(next)
                .then(res => {
                    if (!res.success) writeCache(previous)
                })
                .catch(() => writeCache(previous))
        },
        [userId]
    )

    return { theme, setTheme, themeClass: `sidebar-${theme}` }
}
